import { useState, useEffect } from 'react';
import {
  Box, Button, Typography, TextField, Divider,
} from '@mui/material';
import { TopBar } from 'components';

const BACKUP_KEY = 'volleyballBackups';
const MAX_BACKUPS = 3;

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export function UpdateStore() {
  const [inputData, setInputData] = useState('');
  const [localBackups, setLocalBackups] = useState([]);
  const storedData = localStorage.getItem('reduxStore');

  useEffect(() => {
    const backups = JSON.parse(localStorage.getItem(BACKUP_KEY) || '[]');
    setLocalBackups(backups);
  }, []);

  const saveLocalBackup = () => {
    const backups = JSON.parse(localStorage.getItem(BACKUP_KEY) || '[]');
    const newBackup = {
      id: Date.now(),
      date: new Date().toISOString(),
      data: storedData,
    };
    const updatedBackups = [newBackup, ...backups].slice(0, MAX_BACKUPS);
    localStorage.setItem(BACKUP_KEY, JSON.stringify(updatedBackups));
    setLocalBackups(updatedBackups);
    alert('Backup salvo localmente!');
  };

  const restoreLocalBackup = (backupId) => {
    const backup = localBackups.find((b) => b.id === backupId);
    if (backup) {
      localStorage.setItem('reduxStore', backup.data);
      alert('Backup restaurado! Recarregue a página.');
    }
  };

  const deleteLocalBackup = (backupId) => {
    const updatedBackups = localBackups.filter((b) => b.id !== backupId);
    localStorage.setItem(BACKUP_KEY, JSON.stringify(updatedBackups));
    setLocalBackups(updatedBackups);
  };

  const handleInputChange = (event) => {
    setInputData(event.target.value);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setInputData(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const updateStore = () => {
    try {
      const jsonData = JSON.parse(inputData);
      localStorage.setItem('reduxStore', JSON.stringify(jsonData));
      alert('Store atualizado com sucesso! Recarregue a página.');
    } catch (error) {
      alert('JSON inválido');
    }
  };

  const exportPlayersOnly = () => {
    try {
      const data = JSON.parse(storedData);
      const players = data.players || [];
      // Exportar apenas campos relevantes dos jogadores
      const exportData = players.map((p) => ({
        playerId: p.playerId,
        playerName: p.playerName,
        totalPoints: p.totalPoints ?? (p.wins - p.losses),
        gender: p.gender || 'M',
        playerType: p.playerType || 'Flex',
        wins: p.wins || 0,
        losses: p.losses || 0,
      }));
      return JSON.stringify(exportData, null, 2);
    } catch (error) {
      return '[]';
    }
  };

  const importPlayersOnly = () => {
    try {
      const newPlayers = JSON.parse(inputData);
      const currentData = JSON.parse(storedData || '{}');

      // Atualizar jogadores existentes ou adicionar novos
      const updatedPlayers = newPlayers.map((newPlayer) => {
        const existing = (currentData.players || []).find(
          (p) => p.playerId === newPlayer.playerId || p.playerName === newPlayer.playerName,
        );
        if (existing) {
          return {
            ...existing,
            playerName: newPlayer.playerName || existing.playerName,
            totalPoints: newPlayer.totalPoints
              ?? existing.totalPoints ?? (existing.wins - existing.losses),
            gender: newPlayer.gender || existing.gender || 'M',
            playerType: newPlayer.playerType || existing.playerType || 'Flex',
            wins: newPlayer.wins ?? existing.wins ?? 0,
            losses: newPlayer.losses ?? existing.losses ?? 0,
          };
        }
        // Novo jogador
        return {
          playerId: newPlayer.playerId || `player-${Date.now()}-${Math.random()}`,
          playerName: newPlayer.playerName,
          wins: newPlayer.wins || 0,
          losses: newPlayer.losses || 0,
          teamId: 0,
          isPlaying: false,
          waitingFor: 0,
          checkedIn: false,
          playerType: newPlayer.playerType || 'Flex',
          gender: newPlayer.gender || 'M',
          dailyWins: 0,
          dailyGames: 0,
          totalPoints: newPlayer.totalPoints ?? (newPlayer.wins || 0) - (newPlayer.losses || 0),
        };
      });

      // Manter jogadores que não estão no import
      const existingNotInImport = (currentData.players || []).filter(
        (p) => !newPlayers.some(
          (np) => np.playerId === p.playerId || np.playerName === p.playerName,
        ),
      );

      currentData.players = [...existingNotInImport, ...updatedPlayers];
      localStorage.setItem('reduxStore', JSON.stringify(currentData));
      alert('Jogadores importados com sucesso! Recarregue a página.');
    } catch (error) {
      alert('JSON inválido');
    }
  };

  const downloadExport = () => {
    const dataStr = `data:text/json;charset=utf-8,${encodeURIComponent(storedData)}`;
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', dataStr);
    downloadAnchor.setAttribute('download', 'volleyball-store-backup.json');
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  const downloadPlayersExport = () => {
    const playersData = exportPlayersOnly();
    const dataStr = `data:text/json;charset=utf-8,${encodeURIComponent(playersData)}`;
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', dataStr);
    downloadAnchor.setAttribute('download', 'volleyball-players.json');
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  return (
    <>
      <TopBar />
      <Box sx={{ p: 3, maxWidth: 800, margin: '0 auto' }}>
        <Typography variant="h4" gutterBottom>Gerenciar Dados</Typography>

        <Typography variant="h6" sx={{ mt: 3 }}>Importar Dados</Typography>
        <TextField
          multiline
          rows={6}
          fullWidth
          placeholder="Cole o JSON aqui"
          value={inputData}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
        />
        <input type="file" onChange={handleFileChange} style={{ marginBottom: 16 }} />
        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <Button variant="contained" onClick={updateStore}>
            Importar Store Completo
          </Button>
          <Button variant="outlined" onClick={importPlayersOnly}>
            Importar Apenas Jogadores
          </Button>
        </Box>

        <Typography variant="h6" sx={{ mt: 3 }}>Exportar Dados</Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button variant="contained" onClick={downloadExport}>
            Baixar Backup Completo
          </Button>
          <Button variant="outlined" onClick={downloadPlayersExport}>
            Baixar Apenas Jogadores
          </Button>
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 2 }}>Store Completo:</Typography>
        <TextField
          multiline
          rows={8}
          fullWidth
          value={storedData}
          InputProps={{ readOnly: true }}
          sx={{ mb: 2 }}
        />

        <Typography variant="subtitle2">Jogadores (formato simplificado):</Typography>
        <TextField
          multiline
          rows={8}
          fullWidth
          value={exportPlayersOnly()}
          InputProps={{ readOnly: true }}
        />

        <Divider sx={{ my: 4 }} />

        <Typography variant="h6">Backups Locais (Tablet)</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Salva até 3 backups localmente no dispositivo para restauração rápida.
        </Typography>

        <Button variant="contained" color="success" onClick={saveLocalBackup} sx={{ mb: 3 }}>
          Salvar Backup no Tablet
        </Button>

        {localBackups.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {localBackups.map((backup) => (
              <Box
                key={backup.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Typography>
                  Backup de
                  {' '}
                  {formatDate(backup.date)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => restoreLocalBackup(backup.id)}
                  >
                    Restaurar
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => deleteLocalBackup(backup.id)}
                  >
                    Excluir
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Nenhum backup salvo.
          </Typography>
        )}
      </Box>
    </>
  );
}
